import { useMemo } from 'react';
import { ApolloClient, useApolloClient } from '@apollo/client';
import { PREPARE_SALES_ORDER_ATTACHMENT_UPLOAD } from 'graphql/mutations/salesOrderAttachment';
import { GET_SALES_ORDER_ATTACHMENT_DOWNLOAD_URL } from 'graphql/queries/getSalesOrderAttachmentDownloadUrl';
import { useAttachmentStore } from 'stores/attachmentStore';
import { normalizeMimeType } from 'utils/attachmentUtils';
import type { AttachmentFlow } from './types';

// Excludes local-only files that haven't finished uploading (no uploadUid).
export const buildCurrentAttachmentUids = (orderId: string): string[] =>
  useAttachmentStore
    .getState()
    .attachments.filter((a) => a.entityId === orderId && !!a.uploadUid)
    .map((a) => a.uploadUid as string);

type CreateFlowOptions = {
  onAttachmentChanged?: (contextId: string) => Promise<void> | void;
};

export const createSalesOrderAttachmentFlow = (
  client: ApolloClient<unknown>,
  options: CreateFlowOptions = {}
): AttachmentFlow => ({
  prepareUpload: async (file, contextId) => {
    const res = await client.mutate({
      mutation: PREPARE_SALES_ORDER_ATTACHMENT_UPLOAD,
      variables: {
        orderId: contextId,
        mimeType: normalizeMimeType(file.type),
        fileName: file.name,
      },
    });
    const meta = res.data?.prepareSalesOrderAttachmentUpload;
    if (!meta?.attachmentUid || !meta?.uploadUrl) {
      throw new Error('Prepare upload response is incomplete');
    }
    return { attachmentUid: meta.attachmentUid, uploadUrl: meta.uploadUrl };
  },

  fetchDownloadUrl: async ({ uploadUid, entityId }) => {
    const { data } = await client.query({
      query: GET_SALES_ORDER_ATTACHMENT_DOWNLOAD_URL,
      variables: {
        orderId: entityId,
        attachmentId: uploadUid,
      },
      fetchPolicy: 'network-only',
    });
    const result = data?.salesOrderAttachmentDownloadUrl;
    if (!result) return null;
    return { preSignedUrl: result.preSignedUrl };
  },

  onAttachmentChanged: options.onAttachmentChanged,
});

export const useSalesOrderAttachmentFlow = (
  options: CreateFlowOptions = {}
): AttachmentFlow => {
  const client = useApolloClient();
  const { onAttachmentChanged } = options;
  return useMemo(
    () => createSalesOrderAttachmentFlow(client, { onAttachmentChanged }),
    [client, onAttachmentChanged]
  );
};
